const DEFAULT_TEMPLATE = {
  name: '',
  type: 'pdf',
  isDigitalSignature: false,
  recipientAction: 'receive',
  documentShape: 'shared',
  recipientsChannel: 'internal',
  documentSource: 'fixed',
  signingMode: 'onePerMember',
  sendBy: 'code',
  sendReportTo: { value: -1 },
  defaultMessage: '',
  prefix: '',
  split: false,
  templateAttachment: null,
  layoutSampleAttachment: null,
  fields: [],
  items: [],
  encryptPdfWithDni: false,
};

export const MAPPING_SELECTOR_OPTIONS = ['user', 'fileName', 'xpath', 'coords', 'box'];

/**
 * Routing `items` (extractors) imply personalized delivery and a per-send layout PDF.
 * `split` (single-pdf vs multi-pdf) is chosen in the UI: split by page vs one uploaded PDF per recipient at send time.
 */
export function applyInferredDocumentShape(template) {
  const hasExtractors = (template.items || []).length > 0;
  if (template.type === 'xml') {
    return {
      documentShape: hasExtractors ? 'personalized' : 'shared',
      split: false,
    };
  }
  if (template.type !== 'pdf') {
    return {};
  }
  if (hasExtractors) {
    const out = {
      documentShape: 'personalized',
      documentSource: 'per-send',
    };
    /** Per-send + fields requires layoutSampleAttachment on save; reuse template PDF if user only uploaded that (fixed → inferred per-send). */
    if (!template.layoutSampleAttachment && template.templateAttachment) {
      out.layoutSampleAttachment = template.templateAttachment;
    }
    return out;
  }
  return {
    documentShape: 'shared',
    split: false,
    documentSource: template.documentSource === 'fixed' ? 'fixed' : 'per-send',
  };
}

export function getInitialTemplate(format, defaultMessage) {
  if (!format) {
    const base = { ...DEFAULT_TEMPLATE, defaultMessage };
    return { ...base, ...applyInferredDocumentShape(base) };
  }
  let sendReportTo = { value: -1 };
  if (format?.sendReportToGroup) {
    sendReportTo = { type: 'group', value: format.sendReportToGroup };
  } else if (format?.sendReportToEmployee) {
    sendReportTo = { type: 'employee', value: format.sendReportToEmployee };
  }
  const hasSignatureFields = (format.fields || []).some(f => f?.type === 'signature');
  const isPersonalizedDocument = format.type === 'single-pdf';
  /** Omitted on the wire = backend default `internal` (DocumentFormat schema). */
  const normalizedRecipientsChannel = format.recipientsChannel ?? 'internal';
  const recipientsChannelImpliesSign = normalizedRecipientsChannel !== 'internal';
  let recipientAction =
    format.recipientAction ||
    (hasSignatureFields || recipientsChannelImpliesSign ? 'sign' : 'receive');
  if (recipientAction === 'acknowledge') {
    recipientAction = 'receive';
  }
  const merged = {
    ...DEFAULT_TEMPLATE,
    ...format,
    type: format.type?.split('-').pop() || 'pdf',
    split: format.type === 'single-pdf',
    isDigitalSignature: recipientAction === 'sign',
    recipientAction,
    documentShape: format.documentShape || (isPersonalizedDocument ? 'personalized' : 'shared'),
    recipientsChannel: normalizedRecipientsChannel === 'external' ? 'external' : 'internal',
    documentSource: format.documentSource || 'fixed',
    signingMode: format.signingMode || 'onePerMember',
    templateAttachment: format.templateAttachment?._id || format.templateAttachment || null,
    layoutSampleAttachment:
      format.layoutSampleAttachment?._id || format.layoutSampleAttachment || null,
    sendReportTo,
    fields: Array.isArray(format.fields) ? format.fields : [],
    items: Array.isArray(format.items) ? format.items : [],
  };
  const hasDniItem = (merged.items || []).some(i => i.name === 'dni');
  const hasWidgetFields = (merged.fields || []).length > 0;
  const encryptPdfWithDni =
    typeof format.encryptPdfWithDni === 'boolean'
      ? format.encryptPdfWithDni
      : Boolean(hasDniItem && !hasWidgetFields);
  return { ...merged, encryptPdfWithDni, ...applyInferredDocumentShape(merged) };
}

/**
 * Signing workflows only: expiry + reminders apply when recipients must digitally sign.
 * Same derivation as {@link getInitialTemplate} `isDigitalSignature`.
 * @param {object|null|undefined} format
 */
export function formatRequiresDigitalSignature(format) {
  if (!format || typeof format !== 'object') return false;
  const hasSignatureFields = (format.fields || []).some(f => f?.type === 'signature');
  const normalizedRecipientsChannel = format.recipientsChannel ?? 'internal';
  const recipientsChannelImpliesSign = normalizedRecipientsChannel !== 'internal';
  let recipientAction =
    format.recipientAction ||
    (hasSignatureFields || recipientsChannelImpliesSign ? 'sign' : 'receive');
  if (recipientAction === 'acknowledge') {
    recipientAction = 'receive';
  }
  return recipientAction === 'sign';
}

export function getEditWizardSteps(template) {
  const steps = [];
  if (template.type === 'pdf') {
    steps.push('setupDocument');
  } else {
    steps.push('dataMapping');
  }
  steps.push('review');
  return steps;
}

/** PDF layout and upload always use the setup step (review is message + summary only). */
export function getPdfUploadStepKey(template) {
  if (template.type !== 'pdf') return null;
  return 'setupDocument';
}

function hasPdfAttachment(template) {
  if (template.type !== 'pdf') return true;
  if (template.documentSource === 'fixed') return !!template.templateAttachment;
  return !!(template.layoutSampleAttachment || template.templateAttachment);
}

/** Client-only UI hint from DOM placement; must not be persisted on the format. */
export function stripPlacementSampleFromTemplateItems(items) {
  return (items || []).map(it => {
    if (!it || typeof it !== 'object') return it;
    const rest = { ...it };
    delete rest.placementSample;
    return rest;
  });
}

export function validateStep(stepId, template) {
  if (stepId === 'setupDocument') {
    if (template.type !== 'pdf') return true;
    if (!hasPdfAttachment(template)) return false;
    const personalized = template.documentShape === 'personalized' || template.split;
    if (template.isDigitalSignature) {
      const hasSig = (template.fields || []).some(f => f?.type === 'signature');
      if (!hasSig) return false;
      if (template.signingMode && template.signingMode !== 'onePerMember') {
        const badRouting = (template.items || []).some(
          i =>
            i.selector === 'box' && ['code', 'id', 'dni', 'year', 'month', 'day'].includes(i.name),
        );
        if (badRouting) return false;
      }
      if (personalized) return (template.items || []).length > 0;
      return true;
    }
    if (personalized) {
      return (template.items || []).length > 0;
    }
    if (template.encryptPdfWithDni) {
      const hasDni = (template.items || []).some(i => i.name === 'dni');
      if (!hasDni) return false;
      if ((template.fields || []).length > 0) return false;
    }
    return true;
  }
  if (stepId === 'dataMapping') {
    if (template.type !== 'pdf') return (template.items || []).length > 0;
    return true;
  }
  if (stepId === 'review') {
    if (!template.name?.trim()) {
      return false;
    }
    if (template.type !== 'pdf') return true;
    if (getPdfUploadStepKey(template) !== 'review') return true;
    return hasPdfAttachment(template);
  }
  return true;
}

/**
 * Map AsyncPaginate employee options (`value: { _id, name }`) to the
 * `{ type, value, label }` shape used by {@link buildSendReportToPatch}.
 */
export function normalizeEmployeeReportOptions(options) {
  return (options || []).map(opt => {
    const raw = opt?.value;
    const id = raw && typeof raw === 'object' ? raw._id || raw.id : raw;
    const label =
      opt?.label || (raw && typeof raw === 'object' ? raw.name : '') || String(id ?? '');
    return { type: 'employee', value: id, label };
  });
}

export function buildSendReportToPatch(sendReportTo) {
  let type = sendReportTo?.type;
  let v = sendReportTo?.value;
  // Autosuggest people options historically used value: { _id, name } without type.
  if (v && typeof v === 'object' && (v._id || v.id)) {
    v = v._id || v.id;
    if (!type) type = 'employee';
  }
  const off = !type || v === -1 || v === '-1' || v === undefined || v === null;
  if (off) {
    return { sendReportToGroup: null, sendReportToEmployee: null };
  }
  if (type === 'group') {
    return { sendReportToGroup: v, sendReportToEmployee: null };
  }
  if (type === 'employee') {
    return { sendReportToGroup: null, sendReportToEmployee: v };
  }
  return { sendReportToGroup: null, sendReportToEmployee: null };
}

export function buildSubmitPayload(template) {
  let type = 'multi-pdf';
  if (template.type === 'xml') type = 'xml';
  else if (template.split) type = 'single-pdf';
  const rawRecipientAction =
    template.recipientAction || (template.isDigitalSignature ? 'sign' : 'receive');
  const payload = {
    name: template.name,
    type,
    sendBy: template.sendBy,
    prefix: template.prefix,
    defaultMessage: template.defaultMessage,
    recipientsChannel: 'internal',
    documentSource: template.documentSource || 'fixed',
    signingMode: template.signingMode || 'onePerMember',
    recipientAction: rawRecipientAction === 'acknowledge' ? 'receive' : rawRecipientAction,
    documentShape: template.documentShape || (template.split ? 'personalized' : 'shared'),
    encryptPdfWithDni: Boolean(template.encryptPdfWithDni),
  };
  Object.assign(payload, buildSendReportToPatch(template.sendReportTo));
  if (template.type === 'pdf') {
    payload.items = stripPlacementSampleFromTemplateItems(template.items);
    payload.fields = template.isDigitalSignature ? template.fields || [] : [];
    const docSrc = template.documentSource || 'fixed';
    if (template.templateAttachment) payload.templateAttachment = template.templateAttachment;
    if (template.layoutSampleAttachment) {
      payload.layoutSampleAttachment = template.layoutSampleAttachment;
    } else if (
      docSrc === 'per-send' &&
      template.templateAttachment &&
      Array.isArray(payload.fields) &&
      payload.fields.length > 0
    ) {
      payload.layoutSampleAttachment = template.templateAttachment;
    }
  } else {
    payload.items = stripPlacementSampleFromTemplateItems(template.items);
  }
  return payload;
}
